// Currency utility functions for converting between codes and symbols

// Mapping of common currency codes to their symbols
const currencyCodeToSymbol: Record<string, string> = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'CAD': 'C$',
  'AUD': 'A$',
  'CHF': 'CHF',
  'CNY': '¥',
  'INR': '₹',
  'NGN': '₦',
  'ZAR': 'R',
  'BRL': 'R$',
  'MXN': '$',
  'RUB': '₽',
  'KRW': '₩',
  'TRY': '₺',
  'AED': 'د.إ',
  'SAR': '﷼',
  'THB': '฿',
  'IDR': 'Rp',
  'MYR': 'RM',
  'PHP': '₱',
  'SGD': 'S$',
  'HKD': 'HK$',
  'TWD': 'NT$',
  'VND': '₫',
};

// For ambiguous symbols, map to the most common currency
const ambiguousSymbolToCode: Record<string, string> = {
  '$': 'USD',  // Default $ to USD (most common)
  '¥': 'JPY',  // Default ¥ to JPY (more common than CNY)
  'R': 'ZAR',  // Default R to ZAR (South African Rand)
};

// Reverse mapping from symbols to codes (for unambiguous symbols)
const currencySymbolToCode: Record<string, string> = {};
Object.entries(currencyCodeToSymbol).forEach(([code, symbol]) => {
  // Only add if symbol is not already mapped (skip ambiguous ones)
  if (!currencySymbolToCode[symbol] || ambiguousSymbolToCode[symbol] === code) {
    currencySymbolToCode[symbol] = code;
  }
});

/**
 * Convert currency code to symbol
 * @param code Currency code (e.g., 'USD', 'EUR')
 * @returns Currency symbol (e.g., '$', '€')
 */
export function codeToSymbol(code: string): string {
  return currencyCodeToSymbol[code] || code;
}

/**
 * Convert currency symbol to code
 * @param symbol Currency symbol (e.g., '$', '€')
 * @returns Currency code (e.g., 'USD', 'EUR')
 */
export function symbolToCode(symbol: string): string {
  // First check ambiguous symbols
  if (ambiguousSymbolToCode[symbol]) {
    return ambiguousSymbolToCode[symbol];
  }
  // Then check unambiguous symbols
  return currencySymbolToCode[symbol] || symbol;
}

/**
 * Check if a string is likely a currency symbol
 * @param str String to check
 * @returns True if string is a currency symbol
 */
export function isCurrencySymbol(str: string): boolean {
  return Object.values(currencyCodeToSymbol).includes(str);
}

/**
 * Check if a string is likely a currency code
 * @param str String to check
 * @returns True if string is a currency code
 */
export function isCurrencyCode(str: string): boolean {
  return Object.keys(currencyCodeToSymbol).includes(str);
}

/**
 * Format currency for display
 * @param currency Currency code or symbol
 * @returns Formatted string with both code and symbol if possible
 */
export function formatCurrencyDisplay(currency: string): string {
  if (isCurrencyCode(currency)) {
    const symbol = codeToSymbol(currency);
    return `${currency} (${symbol})`;
  } else if (isCurrencySymbol(currency)) {
    const code = symbolToCode(currency);
    return `${code} (${currency})`;
  }
  return currency;
}

const currencyUtils = {
  codeToSymbol,
  symbolToCode,
  isCurrencySymbol,
  isCurrencyCode,
  formatCurrencyDisplay,
};

export default currencyUtils;
